<template>
    <button v-if="visible" type="button" class="manager-join-badge" title="Ampliar código QR" aria-label="Ampliar el QR para unirse al evento" aria-haspopup="dialog" @click="open_modal">
        <JoinCodeQR :code="active_code" :size="44" />
        <i class="bi bi-arrows-angle-expand" aria-hidden="true"></i>
    </button>

    <!-- Modal enlarged QR - same "code + big QR" shape as Stages.vue's
         modalStageQR, just reachable from this always-mounted badge
         instead of the admin's per-stage row. -->
    <div class="modal fade" id="modalManagerJoinBadge" tabindex="-1" aria-labelledby="modalManagerJoinBadgeLabel" aria-hidden="true" data-backdrop="">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content event-screen join-qr-modal" :style="brand_style">
                <div class="join-qr-modal-header">
                    <div class="join-qr-modal-kicker">Únete al evento</div>
                    <h1 class="join-qr-modal-title m-0" id="modalManagerJoinBadgeLabel">{{ active_label }}</h1>
                    <button type="button" class="join-qr-modal-close" data-bs-dismiss="modal" aria-label="Cerrar"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
                </div>
                <div class="join-qr-modal-body">
                    <p class="join-qr-modal-hint">Escanea el QR con el móvil o introduce este código</p>
                    <div class="join-qr-modal-qr">
                        <JoinCodeQR :code="active_code" :size="260" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from 'bootstrap'
import JoinCodeQR from '@/components/JoinCodeQR.vue'
import utils from '@/../../shared/utils'
import { activeEvent, brandingStyle } from '@/utils/event-branding'

// Small corner reminder of how to join *while a stage is live* (mounted
// once in App.vue so it follows the MANAGER through Title/Activity/Result -
// whichever screen the live question is currently driving them to). The big
// centered QR "como antes" (Home.vue's is_manager_big_screen) already
// covers the no-stage-running/IDLE case - showing this small one too there
// would just be a redundant second QR on the same screen, so it stays
// hidden until a stage is actually playing and switches off again the
// moment it ends (see Stages.vue's "Iniciar etapa"/stage_playback).
// Only PLAYER role ever shows a jersey card in this same bottom-start
// corner (Title.vue/Activity.vue/Result.vue), so it's free for MANAGER.
export default {
    name: 'ManagerJoinBadge',
    components: {
        JoinCodeQR,
    },
    data(){
        return {
            modal: null,
        };
    },
    mounted(){
        this.modal = new Modal('#modalManagerJoinBadge');
    },
    unmounted(){
        this.modal?.hide();
        this.modal?.dispose();
    },
    computed: {
        stage_playback(){
            return this.$store.state.game.stage_playback;
        },
        visible(){
            return window.MODE != utils.APP_MODES.ONLINE
                && this.$store.state.game.user
                && this.$store.state.game.user.role == utils.USER_ROLES.MANAGER
                && !!this.stage_playback;
        },
        active_stage(){
            if(!this.stage_playback)
                return null;
            return (this.$store.state.game.stages || []).find((st) => st.id == this.stage_playback.stage_id) || null;
        },
        active_code(){
            return this.$store.state.game.join_code;
        },
        active_label(){
            const event = activeEvent(this.$store.state.game);
            return event ? event.name : 'Evento';
        },
        // The modal lives outside the router-view that carries the event's
        // palette (App.vue), so it applies the same branding itself.
        brand_style(){
            const event = activeEvent(this.$store.state.game);
            return event ? brandingStyle(event.branding) : {};
        }
    },
    methods: {
        open_modal(){
            this.modal.show();
        }
    }
}
</script>

<style scoped>
.manager-join-badge{
    position: fixed;
    left: 16px;
    bottom: max(10px, env(safe-area-inset-bottom));
    height: 52px;
    display: flex;
    align-items: center;
    gap: 8px;
    color: white;
    border: 0;
    z-index: 1030;
    background: rgba(9, 14, 20, 0.85);
    border-radius: 12px;
    padding: 4px 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
    cursor: pointer;
}

.manager-join-badge-label{
    font-family: "Archivo", sans-serif;
    font-weight: 700;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255, 255, 255, 0.65);
    margin-bottom: 6px;
    text-align: center;
    max-width: 110px;
}

/* JoinCodeQR's own code text has no color of its own by design - it's also
   used on light backgrounds (Panel.vue's modal, Home.vue's big screen,
   Stages.vue's modal) where the default (dark, inherited) text already
   reads fine. This badge's background is dark, so it needs to be forced
   light here specifically instead of dark-on-dark. */
.manager-join-badge :deep(.join-code-qr) { display: flex; align-items: center; gap: 8px; }
.manager-join-badge :deep(.join-code-qr-text){
    font-size: .85rem;
    letter-spacing: .09em;
    margin: 0;
    color: var(--vc-paper-50, #fafaf9);
}

/* Enlarged QR: same card language as the join/team screens (Auth.vue). */
.join-qr-modal{
    border: 0;
    border-radius: 18px;
    overflow: hidden;
    background: var(--vc-paper-50, #fff);
    color: var(--vc-text-900, #1c1c20);
    box-shadow: 0 24px 60px rgba(0, 0, 0, .35);
}
.join-qr-modal-header{
    position: relative;
    padding: 24px 64px 24px 28px;
    background: var(--event-stripes), var(--vc-ink-900, #1c1c20);
    color: var(--event-on-dark, #fff);
}
.join-qr-modal-kicker{
    color: var(--vc-accent-500, #d0142c);
    font-size: .65rem;
    font-weight: 700;
    letter-spacing: .16em;
    text-transform: uppercase;
    margin-bottom: 4px;
}
.join-qr-modal-title{
    font-size: 1.8rem;
    font-weight: 700;
    font-style: italic;
    text-transform: uppercase;
    line-height: 1.05;
    overflow-wrap: anywhere;
}
.join-qr-modal-close{
    position: absolute;
    top: 16px;
    right: 16px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 38px;
    height: 38px;
    padding: 0;
    border: 0;
    border-radius: 9px;
    background: #ffffff15;
    color: inherit;
}
.join-qr-modal-close:hover{ background: #ffffff30; }
.join-qr-modal-close:focus-visible{ outline: 2px solid currentColor; outline-offset: 3px; }
.join-qr-modal-body{
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 28px;
    text-align: center;
}
.join-qr-modal-hint{
    color: var(--vc-text-600, #666);
    font-size: .9rem;
    margin-bottom: 20px;
}
.join-qr-modal-qr{
    padding: 18px 18px 14px;
    background: var(--vc-paper-100, #f3f3f5);
    border-radius: 14px;
}
.join-qr-modal-qr :deep(canvas){
    display: block;
    margin: 0 auto;
    border-radius: 8px;
}
.join-qr-modal-qr :deep(.join-code-qr-text){
    font-family: Arial, Helvetica, sans-serif;
    font-weight: 700;
    font-style: italic;
    font-size: 2.2rem;
    letter-spacing: .18em;
    color: var(--vc-text-900, #1c1c20);
}
</style>
